/**
 * UI State Enumerations
 *
 * Strict typing for UI states across all portals.
 * Use these enums instead of literal strings for consistency and type safety.
 */

// ============================================================================
// LOADING & DATA STATES
// ============================================================================

/**
 * Data loading states for async operations
 */
export enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

/**
 * Mutation states for create/update/delete operations
 */
export enum MutationState {
  IDLE = 'idle',
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

/**
 * Form submission states
 */
export enum FormState {
  PRISTINE = 'pristine',
  DIRTY = 'dirty',
  VALIDATING = 'validating',
  VALID = 'valid',
  INVALID = 'invalid',
  SUBMITTING = 'submitting',
  SUBMITTED = 'submitted',
  ERROR = 'error',
}

// ============================================================================
// TOAST & NOTIFICATION TYPES
// ============================================================================

/**
 * Toast notification severity levels
 */
export enum ToastSeverity {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

/**
 * Toast position on screen
 */
export enum ToastPosition {
  TOP_LEFT = 'top-left',
  TOP_CENTER = 'top-center',
  TOP_RIGHT = 'top-right',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_CENTER = 'bottom-center',
  BOTTOM_RIGHT = 'bottom-right',
}

// ============================================================================
// MODAL & DIALOG STATES
// ============================================================================

/**
 * Modal/Dialog states
 */
export enum ModalState {
  CLOSED = 'closed',
  OPENING = 'opening',
  OPEN = 'open',
  CLOSING = 'closing',
}

/**
 * Confirmation dialog actions
 */
export enum ConfirmAction {
  CONFIRM = 'confirm',
  CANCEL = 'cancel',
  DISMISS = 'dismiss',
}

// ============================================================================
// TABLE & LIST STATES
// ============================================================================

/**
 * Sort direction
 */
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

/**
 * Selection mode for lists/tables
 */
export enum SelectionMode {
  NONE = 'none',
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

/**
 * View mode for data display
 */
export enum ViewMode {
  LIST = 'list',
  GRID = 'grid',
  TABLE = 'table',
  KANBAN = 'kanban',
}

// ============================================================================
// FILTER STATES
// ============================================================================

/**
 * Filter operator types
 */
export enum FilterOperator {
  EQUALS = 'equals',
  NOT_EQUALS = 'not_equals',
  CONTAINS = 'contains',
  NOT_CONTAINS = 'not_contains',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  GREATER_THAN = 'greater_than',
  GREATER_THAN_OR_EQUAL = 'greater_than_or_equal',
  LESS_THAN = 'less_than',
  LESS_THAN_OR_EQUAL = 'less_than_or_equal',
  BETWEEN = 'between',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
}

/**
 * Date range presets for filters
 */
export enum DateRangePreset {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  THIS_QUARTER = 'this_quarter',
  LAST_QUARTER = 'last_quarter',
  THIS_YEAR = 'this_year',
  LAST_YEAR = 'last_year',
  CUSTOM = 'custom',
}

// ============================================================================
// WIZARD & STEP STATES
// ============================================================================

/**
 * Wizard step status
 */
export enum StepStatus {
  PENDING = 'pending',
  CURRENT = 'current',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  ERROR = 'error',
}

/**
 * Navigation direction in wizards
 */
export enum StepDirection {
  FORWARD = 'forward',
  BACKWARD = 'backward',
}

// ============================================================================
// UPLOAD STATES
// ============================================================================

/**
 * File upload states
 */
export enum UploadState {
  IDLE = 'idle',
  SELECTING = 'selecting',
  VALIDATING = 'validating',
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
}

/**
 * File validation status
 */
export enum FileValidationStatus {
  PENDING = 'pending',
  VALID = 'valid',
  INVALID_TYPE = 'invalid_type',
  INVALID_SIZE = 'invalid_size',
  INVALID_CONTENT = 'invalid_content',
}

// ============================================================================
// SIDEBAR & NAVIGATION
// ============================================================================

/**
 * Sidebar state
 */
export enum SidebarState {
  COLLAPSED = 'collapsed',
  EXPANDED = 'expanded',
  HIDDEN = 'hidden',
}

/**
 * Navigation item state
 */
export enum NavItemState {
  DEFAULT = 'default',
  HOVER = 'hover',
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

// ============================================================================
// THEME & APPEARANCE
// ============================================================================

/**
 * Theme mode (also exported from common.ts, kept here for completeness)
 */
export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark',
  MIDNIGHT = 'midnight',
  SYSTEM = 'system',
}

/**
 * Color scheme variants
 */
export enum ColorVariant {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

/**
 * Component size variants
 */
export enum SizeVariant {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

// ============================================================================
// TYPE GUARDS & UTILITIES
// ============================================================================

/**
 * Check if a value is a valid LoadingState
 */
export function isLoadingState(value: string): value is LoadingState {
  return Object.values(LoadingState).includes(value as LoadingState);
}

/**
 * Check if a value is a valid ToastSeverity
 */
export function isToastSeverity(value: string): value is ToastSeverity {
  return Object.values(ToastSeverity).includes(value as ToastSeverity);
}

/**
 * Check if data is in a loading or pending state
 */
export function isLoading(state: LoadingState | MutationState): boolean {
  return state === LoadingState.LOADING || state === MutationState.PENDING;
}

/**
 * Check if operation succeeded
 */
export function isSuccess(state: LoadingState | MutationState): boolean {
  return state === LoadingState.SUCCESS || state === MutationState.SUCCESS;
}

/**
 * Check if operation failed
 */
export function isError(state: LoadingState | MutationState): boolean {
  return state === LoadingState.ERROR || state === MutationState.ERROR;
}
